import { useState } from "react";

import { INTEGRATIONS_MAP } from "features/integrations/constants";
import useCrmSources from "features/integrations/datahooks/useCrmSources";
import type { OutreachSourceType } from "features/integrations/types";
import type { LeadsSearchParams } from "features/leads/types";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerClose,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FormField from "common/components/ui/FormField";
import SelectButton from "common/components/ui/SelectButton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { SelectedFacetParam } from "common/types";

import useLeadExports from "../../../../datahooks/useLeadExports";
import ExportSummary from "./ExportSummary";

import type { OUTREACH_CRM_EXPORT_OPTIONS } from "../../../../constants";
import type { CrmExportSummary } from "../../../../types";

interface ListAndCampaignSelectProps {
  type: OutreachSourceType;
  sourceType: keyof typeof OUTREACH_CRM_EXPORT_OPTIONS;
  onBack: () => void;
  searchParams: LeadsSearchParams<SelectedFacetParam>;
}

export default function ListAndCampaignSelect({
  type,
  sourceType,
  onBack,
  searchParams,
}: ListAndCampaignSelectProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [inputValue, setInputValue] = useState("");
  const [summary, setSummary] = useState<CrmExportSummary>();
  const [selectedOption, setSelectedOption] = useState<{
    id: string;
    name: string;
  }>();

  const { exportLeadsToOutreachCrm, isExportingLeadsToOutreachCrm } =
    useLeadExports();

  const { crmSources, iseLoadingCrmSources } = useCrmSources({
    type,
    sourceType: `${sourceType}s`,
  });

  const { title } = INTEGRATIONS_MAP[type];

  const label = `${title} ${sourceType}`;

  const filteredOptions = crmSources
    ? crmSources.filter(({ name }) =>
        name.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : [];

  function onExport() {
    exportLeadsToOutreachCrm({
      type,
      searchParams,
      sourceType,
      id: selectedOption.id,
    }).then((exportSummary) => setSummary(exportSummary));
  }
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  if (summary)
    return (
      <>
        <Header
          title="Export summary"
          subtitle="A detailed breakdown of your data export"
        />

        <Body>
          <ExportSummary summary={summary} type={type} />

          <Close asChild>
            <Button variant="secondary-black" className="ml-auto">
              Close
            </Button>
          </Close>
        </Body>
      </>
    );

  return (
    <>
      <Header title={<>Select {label}</>} />

      <Body>
        <FormField
          label={`Select an ${label}`}
          htmlFor={`${type}-${sourceType}-select`}
          hint={`Your leads will be exported to the selected ${sourceType}`}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SelectButton
                id={`${type}-${sourceType}-select`}
                hasSelection={!!selectedOption}
                isLoading={iseLoadingCrmSources}
              >
                {selectedOption
                  ? selectedOption.name
                  : `Select a ${sourceType}`}
              </SelectButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[var(--radix-popper-anchor-width)]">
              <RenderIf condition={crmSources?.length > 5}>
                <MenuSearchInput
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              </RenderIf>

              <DropdownMenuList>
                {filteredOptions.length ? (
                  filteredOptions.map((option) => {
                    const { id, name } = option;

                    return (
                      <DropdownMenuItem
                        key={id}
                        onSelect={() => setSelectedOption(option)}
                      >
                        {name}
                      </DropdownMenuItem>
                    );
                  })
                ) : (
                  <p className="mx-4 break-words py-4 text-center text-black-500">
                    No results for{" "}
                    <span className="text-black-950">{inputValue}</span>
                  </p>
                )}
              </DropdownMenuList>
            </DropdownMenuContent>
          </DropdownMenu>
        </FormField>
      </Body>

      <Footer className="items-center justify-end">
        {type === "instantly" && (
          <Button variant="secondary-black" onClick={onBack}>
            Back
          </Button>
        )}

        <Button
          className="ml-auto"
          onClick={onExport}
          disabled={!selectedOption}
          isLoading={isExportingLeadsToOutreachCrm}
        >
          Next
        </Button>
      </Footer>
    </>
  );
}
